import { useSearchParams } from 'react-router';
import { useRef } from 'react';

export type SearchParamValues<TResult> = Partial<Record<keyof TResult, string | undefined>>;

export function useSearchParamState<TResult>(parser: (data: unknown) => TResult) {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();

  // react-router hands each update a fresh copy of the current location's params, so
  // successive calls within one tick would each discard the previous one. Accumulate
  // them here until the location actually changes.
  const pending = useRef({ search, params: new URLSearchParams(search) });

  if (pending.current.search !== search) {
    pending.current = { search, params: new URLSearchParams(search) };
  }

  function setValues(values: SearchParamValues<TResult>) {
    const params = pending.current.params;

    for (const key of Object.keys(values) as (keyof TResult)[]) {
      const value = values[key];

      if (value === undefined) {
        params.delete(key as string);
      } else {
        params.set(key as string, value);
      }
    }

    setSearchParams(new URLSearchParams(params));
  }

  function setValue(key: keyof TResult, value: string | undefined) {
    const values = {} as SearchParamValues<TResult>;
    values[key] = value;

    setValues(values);
  }

  const values = parser(Object.fromEntries(searchParams));

  return {
    setValues,
    setValue,
    values,
  };
}
